/**
 * useSessionTimeout Hook
 * HIPAA Compliance: Automatic session timeout after period of inactivity
 * Regulation: 45 CFR 164.312(a)(2)(iii) - Automatic Logoff
 */

import { useEffect, useCallback, useRef, useState } from 'react';

// Default timeout: 30 minutes (configurable via env REACT_APP_SESSION_TIMEOUT).
// Clamped to 1-120 min for safety.
const RAW_MINUTES = parseInt(process.env.REACT_APP_SESSION_TIMEOUT || '30', 10);
const SESSION_TIMEOUT_MINUTES = Number.isFinite(RAW_MINUTES)
  ? Math.min(Math.max(RAW_MINUTES, 1), 120)
  : 30;
const SESSION_TIMEOUT = SESSION_TIMEOUT_MINUTES * 60 * 1000;
const WARNING_BEFORE = 60 * 1000; // Show warning 1 minute before timeout

/**
 * Hook that tracks user activity and triggers timeout/warning callbacks
 * @param {Object} options
 * @param {Function} options.onTimeout - Called when session times out
 * @param {Function} options.onWarning - Called when warning should be shown
 * @param {boolean} options.enabled - Whether timeout tracking is enabled
 * @returns {Object} - { resetTimer, remainingTime, isWarningVisible }
 */
export const useSessionTimeout = ({ onTimeout, onWarning, enabled = true }) => {
  const timeoutRef = useRef(null);
  const warningRef = useRef(null);
  const [remainingTime, setRemainingTime] = useState(SESSION_TIMEOUT);
  const [isWarningVisible, setIsWarningVisible] = useState(false);
  const countdownRef = useRef(null);
  // Ref sincronizado con isWarningVisible para que los event listeners lo
  // puedan leer sin que su closure quede obsoleto — y sin necesidad de
  // incluir isWarningVisible en las deps del useEffect (lo cual causaba
  // que el effect re-ejecutara resetTimer() al aparecer el warning,
  // cerrándolo inmediatamente y abortando el flujo de timeout).
  const isWarningVisibleRef = useRef(false);
  useEffect(() => {
    isWarningVisibleRef.current = isWarningVisible;
  }, [isWarningVisible]);

  const clearAllTimers = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    clearAllTimers();
    setIsWarningVisible(false);
    setRemainingTime(SESSION_TIMEOUT);

    // Set warning timer
    warningRef.current = setTimeout(() => {
      setIsWarningVisible(true);
      onWarning?.();

      // Start countdown
      let timeLeft = WARNING_BEFORE;
      setRemainingTime(timeLeft);

      countdownRef.current = setInterval(() => {
        timeLeft -= 1000;
        setRemainingTime(timeLeft);

        if (timeLeft <= 0) {
          clearInterval(countdownRef.current);
        }
      }, 1000);
    }, SESSION_TIMEOUT - WARNING_BEFORE);

    // Set timeout timer
    timeoutRef.current = setTimeout(() => {
      clearAllTimers();
      setIsWarningVisible(false);
      onTimeout();
    }, SESSION_TIMEOUT);
  }, [enabled, onTimeout, onWarning, clearAllTimers]);

  const extendSession = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      return;
    }

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'mousemove',
      'click'
    ];

    // Throttle to avoid excessive timer resets
    let lastActivity = Date.now();
    const throttleMs = 1000; // Only reset timer once per second max

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity > throttleMs) {
        lastActivity = now;
        // Only reset if warning is not visible — cuando el modal está
        // abierto, el usuario debe elegir explícitamente "Continue Working"
        // o "Logout Now"; el movimiento del mouse no extiende la sesión.
        if (!isWarningVisibleRef.current) {
          resetTimer();
        }
      }
    };

    // Add event listeners
    activityEvents.forEach(event =>
      document.addEventListener(event, handleActivity, { passive: true })
    );

    // Start initial timer
    resetTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event =>
        document.removeEventListener(event, handleActivity)
      );
      clearAllTimers();
    };
    // NOTA: isWarningVisible NO va en deps — se lee vía isWarningVisibleRef
    // para evitar que el effect se re-ejecute y reinicie los timers cuando
    // aparezca el warning.
  }, [enabled, resetTimer, clearAllTimers]);

  return {
    resetTimer,
    extendSession,
    remainingTime,
    isWarningVisible,
    remainingSeconds: Math.ceil(remainingTime / 1000)
  };
};

export default useSessionTimeout;
